// ============================================================
// PLUMME — AI Advisor: report block contract
// ============================================================
// The shared vocabulary the report sections are written in. Kept separate from
// the sections themselves so the two builder files cannot drift apart on what
// a block looks like.
//
// Text is emitted as { key, vars } with the FULL i18n key and resolved with t()
// in the page. That is the same contract aiAdvisorNarrative and
// aiAdvisorBriefing use, and carrying the full key lets a section reuse an
// existing catalogue entry (adv.feed.*) without prefix juggling.
//
// A section with nothing to show emits an `empty` block. It never emits a
// placeholder figure, and it never omits itself silently — a reader has to be
// able to tell "nothing happened" apart from "we could not measure this".
//
// Block contract:
//   { type: 'text',      key, vars }
//   { type: 'note',      tone, key, vars }
//   { type: 'empty',     key, vars }
//   { type: 'list',      items: [{ key, vars } | { text }] }
//   { type: 'kpis',      items: [{ id, label:{key,vars}, value, format,
//                                  delta, deltaTone, compare:{key,vars} }] }
//   { type: 'metrics',   items: [{ id, label:{key,vars}, value, format, tone }] }
//   { type: 'table',     cols: [{key,vars}], rows: [[cell, ...]] }
//   { type: 'namedList', items: [{ id, name, value, format }] }
//   { type: 'risks',     items: [{ id, severity, title, body }] }
//   { type: 'actions',   items: [{ id, recId, priority, title, impact, route }] }
//   { type: 'decisions', items: [{ id, label:{key,vars} }] }
//   { type: 'sources',   chips: [{key,vars}], meta: [{key,vars}] }
//   { type: 'chart',     variant: 'bars' | 'line' | 'waterfall', format, compact,
//                        categories: [cell], series: [{ id, labelKey, values }],
//                        opening, closing }   // the last two: waterfall only
// A table cell is { text } | { key, vars } | { value, format, tone }.
const PlummeAiAdvisorReportBlocks = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const round = (value) => Math.round(num(value));
  const round1 = (value) => Math.round(num(value) * 10) / 10;

  // Percent change is undefined against a zero base — going from nothing to
  // something is not "infinite growth", it is a first year. Callers render the
  // null as an explicit "no comparison basis" rather than as 0 %.
  const percentChange = (now, was) => (num(was) === 0 ? null : round1((num(now) - num(was)) / Math.abs(num(was)) * 100));
  const deltaTone = (value, betterWhen = 'high') => {
    if (value == null || value === 0) return 'neutral';
    const good = betterWhen === 'high' ? value > 0 : value < 0;
    return good ? 'up' : 'down';
  };

  const text = (key, vars) => ({ type: 'text', key, vars });
  const note = (tone, key, vars) => ({ type: 'note', tone, key, vars });
  const empty = (key, vars) => ({ type: 'empty', key, vars });
  const money = (value) => ({ value: round(value), format: 'currency' });
  // A chart never carries a figure the surrounding blocks do not also state in
  // text. It is a second reading of the same numbers, so a reader who cannot
  // see it -- in the PDF, in a screen reader, in a printout -- loses nothing.
  const chart = (variant, config) => ({ type: 'chart', variant, format: 'currency', ...config });
  const section = (id, titleKey, blocks) => ({ id, titleKey, blocks: blocks.filter(Boolean) });

  const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  // Variable names that carry an amount. The page formats these with the locale
  // money formatter before handing them to t(), the same way aiiFeedVars does
  // for the advisor feed. Builder and renderer have to agree on the list, so it
  // lives here and is exported rather than duplicated.
  const MONEY_VARS = ['income', 'result', 'amount', 'buffer', 'lowest', 'net', 'vat'];
  const SEVERITY_BY_LEVEL = { hoy: 'critical', middels: 'watch', lav: 'info' };
  const PRIORITY_BY_LEVEL = { crit: 'high', imp: 'medium', opp: 'low' };

  return {
    num, safeArray, round, round1, percentChange, deltaTone,
    text, note, empty, money, chart, section,
    MONTH_KEYS, MONEY_VARS, SEVERITY_BY_LEVEL, PRIORITY_BY_LEVEL,
  };
})();

Object.assign(window, { PlummeAiAdvisorReportBlocks });
